import { useEffect, useMemo, useState } from "react";
import { makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import caseStudyService from "../../../services/CaseStudyService";
import CaseStudyTile from "./CaseStudyTile";
import Hero from "../../../components/hero/Hero";
import { useTranslation } from "react-i18next";
import Categories from "./Categories";
import Status from "../../../enums/Status";
import ICaseStudyPreview from "../../../models/caseStudy/ICaseStudyPreview";
import { useHeroMainSnap } from "../../../hooks/useHeroMainSnap";
import Industries from "./Industries";
import SEO from "../../../app/SEO";

const useStyles = makeStyles({
    mainContainer: {
        padding: 0
    },
    cardsContainer: {
        display: "flex",
        flexWrap: "wrap",
        rowGap: "16px",
        columnGap: "48px",
        padding: "32px",
        maxWidth: "100%",
        margin: "0 auto",
        justifyContent: "center",

        "@media (min-width: 1200px)": {
            maxWidth: "1200px"
        }
    },
    skeleton: {
        width: "100%",
        maxWidth: "350px",
        height: "300px"
    },
    skeletonItem: {
        width: "100%",
        height: "100%"
    }
});

export default function CaseStudies() {
    const { t } = useTranslation("casestudies");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [caseStudies, setCaseStudies] = useState<ICaseStudyPreview[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

    useEffect(() => {
        fetchCaseStudies(setCaseStudies, setStatus);
    }, []);

    useHeroMainSnap("casestudies-hero", "casestudies-main", {
        duration: 900,
        easing: "quart",
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    const onSelectedCategoriesChange = (categories: string[]) => setSelectedCategories(categories);

    const filteredCaseStudies = useMemo(() => {
        return caseStudies.filter(c =>
            (selectedCategories.length === 0 || c.tags.some(tag => selectedCategories.includes(tag))) &&
            (selectedIndustries.length === 0 || selectedIndustries.includes(c.client.industry))
        );
    }, [caseStudies, selectedCategories, selectedIndustries]);

    const sortedFilteredCaseStudies = useMemo(() => {
        const toTs = (published: string): number => {
            const ts = new Date(published).getTime();
            return Number.isFinite(ts) ? ts : -Infinity;
        };

        return [...filteredCaseStudies].sort((a, b) => toTs(b.published) - toTs(a.published));
    }, [filteredCaseStudies]);

    return (
        <>
            <SEO
                title={{ pageTitle: t("hero.pageTitle"), title: t("hero.pageTitle") }}
                canonicalPath="/casestudies"
            />
            <div id="casestudies-hero">
                <Hero
                    title={t("hero.title")}
                    subtitle={t("hero.subtitle")}
                    imageUri="/img/banners/case-studies.jpg"
                />
            </div>

            <div id="casestudies-main" className={styles.mainContainer}>
                <Categories
                    selectedCategories={selectedCategories}
                    setSelectedCategories={onSelectedCategoriesChange}
                />

                <Industries
                    caseStudies={caseStudies}
                    status={status}
                    selectedIndustries={selectedIndustries}
                    setSelectedIndustries={setSelectedIndustries}
                />

                <div className={styles.cardsContainer}>
                    {status === Status.LOADING &&
                        Array.from({ length: 6 }, (_, i) => (
                            <Skeleton key={i} className={styles.skeleton}>
                                <SkeletonItem className={styles.skeletonItem} />
                            </Skeleton>
                        ))}

                    {status === Status.LOAD_SUCCESS &&
                        sortedFilteredCaseStudies.map((c) => (
                            <CaseStudyTile key={c.id} caseStudy={c} />
                        ))}
                </div>
            </div>
        </>
    );
}

async function fetchCaseStudies(
    setCaseStudies: (caseStudies: ICaseStudyPreview[]) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const caseStudies = await caseStudyService.getCaseStudyPreviews();
        setCaseStudies(caseStudies);
        setStatus(Status.LOAD_SUCCESS);
    } catch {
        setStatus(Status.LOAD_ERROR);
    }
}
